import React, { useEffect, useState } from 'react';
import { onSnapshot, collection, query, where, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import StyledTable from './StyledTable';
import { highlightMatch } from '../utils/highlightMatch';
import { useTenant } from "../context/TenantContext";
import ConfirmDialog from './ConfirmDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { toast } from 'react-toastify';

library.add(fab, faTrash);

const PatientList = () => {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const { tenantId, loading } = useTenant();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);

  useEffect(() => {
    if (loading || !tenantId) return;

    const q = query(collection(db, "patients"), where("tenantId", "==", tenantId));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const livePatients = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPatients(livePatients);
    });

    return () => unsubscribe();
  }, [tenantId, loading]);

  const handleDeleteClick = (patient) => {
    setPatientToDelete(patient);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!patientToDelete) return;

    try {
      await deleteDoc(doc(db, 'patients', patientToDelete.id));
      toast.success('Patient deleted successfully');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete patient');
    } finally {
      setConfirmOpen(false);
      setPatientToDelete(null);
    }
  };

  const filtered = patients.filter(p =>
    (p.name?.toLowerCase().includes(search.toLowerCase()) ||
     String(p.phone || '').includes(search))
  );

  return (
    <div className="patient-list-container">
      <div className="search-container">
        <input 
          type="text" 
          placeholder="Search by name or phone..." 
          value={search}
          onChange={e => setSearch(e.target.value)} 
        />
      </div>

      <StyledTable
        headers={['Name', 'Phone', 'Age', 'Gender', 'Email', 'Address', 'Actions']}
        data={filtered}
        renderRow={(p) => [
          <div key="name" data-tooltip={p.name}>{highlightMatch(p.name, search)}</div>,
          <div key="phone" data-tooltip={p.phone}>{highlightMatch(p.phone, search)}</div>,
          <div key="age">{p.age || '-'}</div>,
          <div key="gender">{p.gender}</div>,
          <div key="email" data-tooltip={p.email}>{highlightMatch(p.email, search)}</div>,
          <div key="address" data-tooltip={p.address}>{highlightMatch(p.address, search)}</div>,
          <div key="action" className="delete-btn" onClick={() => handleDeleteClick(p)}>
            <FontAwesomeIcon icon="trash" className="unauth-trash-icon" />
          </div>
        ]}
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Delete Patient"
        message={`Are you sure you want to delete "${patientToDelete?.name}"?`}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default PatientList;
